'use client';
import React, { useState } from 'react';

interface GuardianLight {
  id: string;
  x: number;
  y: number;
  label: string;
}

const initialLights: GuardianLight[] = [
  { id: 'gl-001', x: 22, y: 38, label: 'Humpback family' },
  { id: 'gl-002', x: 55, y: 52, label: 'Dolphin pod' },
  { id: 'gl-003', x: 72, y: 28, label: 'Sardine school' },
  { id: 'gl-004', x: 38, y: 70, label: 'Plankton bloom' },
  { id: 'gl-005', x: 15, y: 60, label: 'Blue whale' },
  { id: 'gl-006', x: 84, y: 55, label: 'Coral shelf' },
  { id: 'gl-007', x: 48, y: 22, label: 'Sperm whale' },
  { id: 'gl-008', x: 65, y: 75, label: 'Sea turtle' },
];

const wildlifeArcs = [
  { id: 'wa-01', cx: '22%', cy: '38%', label: '🐋', name: 'Humpback', color: 'var(--kid-dolphin)' },
  { id: 'wa-02', cx: '55%', cy: '52%', label: '🐬', name: 'Dolphins', color: 'var(--kid-dolphin)' },
  { id: 'wa-03', cx: '72%', cy: '28%', label: '🐟', name: 'Sardines', color: 'var(--kid-fish)' },
  { id: 'wa-04', cx: '38%', cy: '70%', label: '✨', name: 'Plankton', color: 'var(--kid-plankton)' },
];

export default function GuardianMapArea() {
  const [lights, setLights] = useState<GuardianLight[]>(initialLights);
  const [placing, setPlacing] = useState(false);

  const handleMapClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!placing) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const newLight: GuardianLight = {
      id: `gl-${Date.now()}`,
      x,
      y,
      label: 'New guardian light',
    };
    setLights((prev) => [...prev, newLight]);
    setPlacing(false);
  };

  return (
    <div className="bg-card/40 border border-border/50 rounded-2xl overflow-hidden backdrop-blur-sm">
      <div className="px-5 py-4 border-b border-border/50 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Ocean Map</h2>
          <p className="text-xs text-kid-blue/70">{lights.length} guardian lights placed · 4 creatures nearby</p>
        </div>
        <button
          onClick={() => setPlacing(!placing)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 ${
            placing
              ? 'bg-kid-blue/20 border border-kid-blue/40 text-kid-blue' :'bg-kid-ocean/20 border border-kid-ocean/30 text-kid-blue hover:bg-kid-ocean/30'
          }`}
        >
          <span>💙</span>
          {placing ? 'Tap map to place...' : 'Place Guardian Light'}
        </button>
      </div>

      {/* Map canvas */}
      <div
        className="relative w-full"
        style={{ height: 320, cursor: placing ? 'crosshair' : 'default', background: 'linear-gradient(180deg, rgba(2,24,32,0.8) 0%, rgba(3,30,42,0.9) 100%)' }}
        onClick={handleMapClick}
        role="img"
        aria-label="Ocean guardian map with wildlife positions and guardian lights"
      >
        {/* Ocean grid lines (subtle depth lines) */}
        <svg className="absolute inset-0 w-full h-full" aria-hidden="true">
          {[20, 40, 60, 80].map((y) => (
            <line key={`gridY-${y}`} x1="0" y1={`${y}%`} x2="100%" y2={`${y}%`} stroke="rgba(0,119,182,0.08)" strokeWidth="1" />
          ))}
          {[25, 50, 75].map((x) => (
            <line key={`gridX-${x}`} x1={`${x}%`} y1="0" x2={`${x}%`} y2="100%" stroke="rgba(0,119,182,0.06)" strokeWidth="1" />
          ))}

          {/* Wildlife arc rings */}
          {wildlifeArcs.map((arc) => (
            <g key={arc.id}>
              <circle cx={arc.cx} cy={arc.cy} r="18" fill="none" stroke={arc.color} strokeWidth="1" strokeDasharray="4 6" opacity="0.4" className="wildlife-arc" />
              <circle cx={arc.cx} cy={arc.cy} r="30" fill="none" stroke={arc.color} strokeWidth="0.5" strokeDasharray="3 8" opacity="0.2" className="wildlife-arc" />
            </g>
          ))}
        </svg>

        {/* Wildlife labels */}
        {wildlifeArcs.map((arc) => (
          <div
            key={`label-${arc.id}`}
            className="absolute flex flex-col items-center pointer-events-none"
            style={{ left: arc.cx, top: arc.cy, transform: 'translate(-50%, -50%)' }}
          >
            <span className="text-2xl">{arc.label}</span>
            <span className="text-[9px] font-medium mt-0.5" style={{ color: arc.color }}>{arc.name}</span>
          </div>
        ))}

        {/* Guardian lights */}
        {lights.map((light) => (
          <div
            key={light.id}
            className="absolute pointer-events-none"
            style={{ left: `${light.x}%`, top: `${light.y}%`, transform: 'translate(-50%, -50%)' }}
            title={light.label}
          >
            <div
              className="w-4 h-4 rounded-full guardian-light"
              style={{ background: 'radial-gradient(circle, rgba(76,201,240,0.9) 0%, rgba(76,201,240,0.2) 60%, transparent 100%)' }}
            />
          </div>
        ))}

        {/* Depth legend */}
        <div className="absolute bottom-3 right-3 flex flex-col gap-1">
          {[
            { id: 'dep-shallow', color: 'rgba(76,201,240,0.4)', label: 'Shallow' },
            { id: 'dep-mid', color: 'rgba(0,119,182,0.4)', label: 'Mid' },
            { id: 'dep-deep', color: 'rgba(3,30,42,0.8)', label: 'Deep' },
          ].map((d) => (
            <div key={d.id} className="flex items-center gap-1.5">
              <span className="w-3 h-2 rounded-sm" style={{ background: d.color }} />
              <span className="text-[9px] text-muted-foreground">{d.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}